import { useEffect } from "react";

/**
 * useStructuredData — declaratively inject a JSON-LD <script> tag into the
 * document head for Schema.org structured data. Mounts on render, removes
 * on unmount so route changes don't leave stale schemas behind.
 *
 * Why this exists: the 2026 SEO sweep wired per-route titles + descriptions +
 * canonical URLs (usePageMeta) and content padding into the body, but Google's
 * rich-results layer needs structured data to know WHAT KIND of page this is —
 * a LocalBusiness (driving the knowledge panel, map listing, hours, ratings),
 * an Organization (the brand knowledge graph), an OfferCatalog (so service
 * prices can surface in search), a CollectionPage, etc. Without JSON-LD,
 * Allure's portfolio ranks as "some page with photos" instead of "the editorial
 * photography studio at 212 Hawthorne Lane, Newark NJ, by appointment only,
 * starting at $1,400."
 *
 * Implementation: zero dependency. We just upsert a single
 * <script type="application/ld+json" data-page-schema="<id>"> per call,
 * so a page that mounts multiple schemas (e.g. LocalBusiness + OfferCatalog)
 * gets separate tags rather than a clobbering single tag. On unmount the
 * exact tag we added gets removed.
 *
 * @param {object|object[]} schema  — one schema object or an array of them.
 *                                    Pass `null`/`undefined` to skip cleanly.
 * @param {string} [id]             — optional disambiguator if a page mounts
 *                                    multiple kinds of schemas; defaults to
 *                                    the schema's `@type` value.
 */
export function useStructuredData(schema, id) {
  useEffect(() => {
    if (!schema) return undefined;
    // Normalize to array
    const schemas = Array.isArray(schema) ? schema : [schema];
    const els = [];
    for (const s of schemas) {
      if (!s || typeof s !== "object") continue;
      const slot = id || (Array.isArray(s["@type"]) ? s["@type"].join("-") : s["@type"]) || "schema";
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-page-schema", slot);
      el.textContent = JSON.stringify(s);
      document.head.appendChild(el);
      els.push(el);
    }
    return () => {
      for (const el of els) {
        try { el.remove(); } catch (_) { /* head already torn down */ }
      }
    };
  }, [JSON.stringify(schema), id]);
}

export default useStructuredData;
